import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import {
  OCCUPATION_FORMS,
  OccupationForm,
  OccupationSeries
} from './models/occupation.model';
import { OccupationChartComponent } from './occupation-chart/occupation-chart.component';
import { OccupationFormService } from './occupation-form.service';
import { OccupationLegendComponent } from './occupation-legend/occupation-legend.component';
import { OccupationTableComponent } from './occupation-table/occupation-table.component';

/**
 * The occupation visualization module. Dumb about where the series come from: the store
 * aggregates them across every loaded entity, because a dock is contested between routes.
 */
@Component({
  selector: 'app-occupation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OccupationChartComponent,
    OccupationLegendComponent,
    OccupationTableComponent,
    TranslatePipe
  ],
  templateUrl: './occupation.component.html',
  styles: ``
})
export class OccupationComponent {
  private readonly formService = inject(OccupationFormService);
  protected readonly ICONS = ICONS;
  protected readonly forms = OCCUPATION_FORMS;

  readonly series = input<OccupationSeries[]>([]);
  readonly loading = input<boolean>(false);

  /** Empty means every site; picking sites compares them on one ratio scale. */
  private readonly pickedSites = signal<ReadonlySet<string>>(new Set());
  protected readonly showTable = signal(false);

  protected readonly form = this.formService.formFor('module');

  protected readonly siteUids = computed(() => [
    ...new Set(this.series().map((s) => s.siteUid))
  ]);

  /**
   * A pick survives only while its site is still charted. Left alone, changing the table
   * selection strands the filter on a site no longer in the series and empties the view
   * under a chip bar that still looks live.
   */
  protected readonly selectedSites = computed(() => {
    const available = new Set(this.siteUids());
    return new Set([...this.pickedSites()].filter((uid) => available.has(uid)));
  });

  protected readonly visibleSeries = computed(() => {
    const selected = this.selectedSites();
    if (selected.size === 0) return this.series();
    return this.series().filter((s) => selected.has(s.siteUid));
  });

  protected setForm(form: OccupationForm): void {
    this.formService.set('module', form);
  }

  protected toggleSite(uid: string): void {
    const next = new Set(this.selectedSites());
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    this.pickedSites.set(next);
  }

  protected toggleTable(): void {
    this.showTable.update((shown) => !shown);
  }
}
